import { useCallback, useId } from 'react';
import FormField, { FormFieldProps } from 'fe-design-base/atoms/FormField';
import FDBDatePicker from 'fe-design-base/organisms/FDBDatePicker';
import { Field, FormikValues, useFormikContext } from 'formik';

export type FDBDatePickerFieldProps = Omit<
  FormFieldProps,
  'children' | 'size'
> & {
  dataTestId?: string;
  blockDatesBefore?: string;
  blockDatesAfter?: string;
  value?: string | null;
  shouldDisableDates?: (day: moment.Moment) => boolean;
};

const FDBDatePickerField = ({
  name,
  label,
  inputId,
  width,
  fullWidth,
  labelPosition,
  labelWidth,
  shrink,
  disabled,
  readOnly,
  helperText,
  hasAsterisk,
  successText,
  blockDatesBefore,
  blockDatesAfter,
  dataTestId,
  ...inputProps
}: FDBDatePickerFieldProps) => {
  const fieldId = useId();
  const { touched, errors, setFieldTouched } = useFormikContext<FormikValues>();
  const error = touched?.[name] && errors?.[name] && !disabled;

  const handleBlur = useCallback(() => {
    setFieldTouched(name, true);
  }, [name, setFieldTouched]);

  return (
    <FormField
      label={label}
      labelPosition={labelPosition}
      labelWidth={labelWidth}
      name={name}
      width={width}
      fullWidth={fullWidth}
      shrink={shrink}
      disabled={disabled}
      readOnly={readOnly}
      inputId={inputId || fieldId}
      helperText={helperText}
      successText={successText}
      hasAsterisk={hasAsterisk}
      labelTopShift="-30px"
    >
      <Field
        as={FDBDatePicker}
        name={name}
        label={label}
        disabled={disabled}
        readOnly={readOnly}
        inputId={inputId || fieldId}
        onBlur={handleBlur}
        error={error}
        blockDatesBefore={blockDatesBefore}
        blockDatesAfter={blockDatesAfter}
        dataTestId={dataTestId}
        {...inputProps}
      />
    </FormField>
  );
};

export default FDBDatePickerField;
